import {
  MutationActions,
  useEntityMutation,
  QueryKeys,
  queryClient,
} from "@/api/core";
import { useApi } from "@/hooks";
import { type Status, statusSchema } from "@/schemas";
import { removePageItem } from "@/utils/queries";

import { updateStatusCache } from "../../statuses/utils";
import { SuppressionQueryKeys } from "../query-keys";

import type { ApiErrorResponse } from "@/types/api";

interface Variables {
  statusId: string;
  groupId: string;
}

function useUnsuppressGroup() {
  const api = useApi();

  return useEntityMutation({
    globalQueryKey: QueryKeys.statuses.global,
    idAttributeFromVariables: "statusId",
    action: MutationActions.UPDATE,
    schema: statusSchema,
    skipOnSuccess: true,
    mutationFn: (variables: Variables) =>
      api.delete(
        `/api/v1/recommendations/groups/suppressions/${variables.groupId}`,
      ),
    onMutate(variables) {
      const [prevStatus] = updateStatusCache(variables.statusId, () => ({
        interested: true,
      }));

      return { prevStatus };
    },
    onSuccess(_data, _variables, context) {
      if (context?.prevStatus) {
        // Remove the Status from the list of supressed statuses.
        removePageItem(
          SuppressionQueryKeys.statuses,
          context?.prevStatus,
          (o: Status, n: Status) => o.id === n.id,
        );
      }
    },
    onError(error: ApiErrorResponse, variables, context) {
      // This happens if a user tries to unsupress two statuses from the
      // same group. Instead of resetting the Status cache, let's just proceed
      // as if the request was successful.
      const hasAlreadyUnsupressedGroup = error.response?.status === 404;
      if (hasAlreadyUnsupressedGroup) {
        return;
      }

      if (context) {
        queryClient.setQueryData(
          QueryKeys.statuses.detail(variables.statusId),
          context.prevStatus,
        );
      }
    },
  });
}

export { useUnsuppressGroup };
